import {takeLatestF, putAll, showSuccess, extractFormErrorsFromResponse, NullableKeys} from '@fl/cmsch-fe-library';
import {SagaIterator} from 'redux-saga';
import {genNakedPropOrCrash, opt} from 'ts-opt';
import {call, put, select} from 'typed-redux-saga';

import {Api} from 'api/gen/Api';
import {CreateBreederBinding} from 'api/gen/CreateBreederBinding';
import {t} from 'app/translations';
import {formHelpers} from 'utils/forms';
import {showBeError} from 'utils/show-be-error';

import {
    subjectManagementFacilityFormName,
    SubjectManagementFacilityFormValues,
} from '../../types/subject-management-facility-form-values';
import {CreateManagementBreederAction, adminAction} from '../action';
import {
    buildSubjectManagementCenterOptions,
    BuildSubjectManagementCenters,
} from './build-subject-management-center-options';

const te = t('common');
const title = te('createEntry');

const getCreateBreederApiValues = (
    formValues: NullableKeys<SubjectManagementFacilityFormValues>,
): CreateBreederBinding => {
    const get = genNakedPropOrCrash(formValues);

    return {
        approvedFrom: get('approvedFrom'),
        approvedTo: opt(formValues).prop('approvedTo').orNull(),
        roleIds: get('roleIds'),
        breederId: get('subjectId'),
        userProfileId: get('userProfileId'),
        farmIds: get('farmIds'),
    };
};

function* execute({payload: {breederId}}: CreateManagementBreederAction): SagaIterator {
    const formValues = yield* select(state => formHelpers.formValues(subjectManagementFacilityFormName)(state)
        .orCrash(`no form values: ${subjectManagementFacilityFormName}`));
    yield* put(formHelpers.startSubmit(subjectManagementFacilityFormName));

    const buildFarms: BuildSubjectManagementCenters = {centerIds: formValues.farmIds, type: 'USERS_FARM'};

    const farmIds = yield* call(buildSubjectManagementCenterOptions, buildFarms);

    const response = yield* call(Api.addUserProfileBreederRoleBySubjectManager, getCreateBreederApiValues({
        centerIds: null,
        approvedFrom: formValues.approvedFrom,
        approvedTo: formValues.approvedTo,
        roleIds: formValues.roleIds,
        subjectId: formValues.subjectId,
        userProfileId: formValues.userProfileId,
        userProfileRoleIds: formValues.userProfileRoleIds,
        farmIds,
        tag: formValues.tag,
    }));

    if (response.isSuccess) {
        yield* put(adminAction.getManagementBreedersTableData(opt({action: 'refresh'}), opt(breederId)));
        yield* put(showSuccess(title, te('createEntrySuccess'), false, 'createManagementBreeder'));
        yield* put(adminAction.setManagementSubjectModalVisible(null));
        yield* put(formHelpers.reset(subjectManagementFacilityFormName));
    } else {
        yield putAll(showBeError(response, title));
        yield* put(formHelpers.stopSubmit(subjectManagementFacilityFormName, extractFormErrorsFromResponse(response)));
    }
}

/** Runs the admin saga that creates a breeder-management binding from the admin form. */
export function* createManagementBreederSaga(): SagaIterator {
    yield takeLatestF('admin/CREATE_MANAGEMENT_BREEDER', execute);
}
